/**
 * Het Acties-menu bij De zaak (ontwerp v4 §3, contract §2.3).
 *
 * Eén menu per zaak, gegroepeerd: Geld / Order en zending / Vervoerder
 * / Klant. Elke actie loopt langs dezelfde drie stappen uit de
 * actie-laag: voorstellen, bevestigen, uitvoeren.
 *
 * Twee harde regels van deze repo staan hier in code:
 *
 * - **Uitgeschakeld = uitleggen waarom.** Een actie met stand
 *   `nog_niet_aangesloten` staat uit mét de reden in beeld, nooit
 *   stil verborgen.
 * - **Een droge actie zegt dat hij droog is.** Na uitvoering met
 *   `droog: true` staat er "nog niet doorgevoerd" in plaats van een
 *   groene bevestiging.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import {
  werkplekApi,
  type ActieSoort,
  type WerkplekActie,
} from "@/lib/werkplek-api";

import { AfrondBalk, AfrondKeuze, useAfronden } from "../Tegel/Afronden";

import { IcoonBeheer, IcoonPijlOnder } from "./pictogrammen";
import { Zwevendmenu } from "./Zwevendmenu";
import "./vandaag.css";

const GROEPKOPPEN: Record<string, string> = {
  geld: "Geld",
  order_zending: "Order en zending",
  vervoerder: "Vervoerder",
  klant: "Klant",
  wekker: "Wekker",
};
const GROEPVOLGORDE = ["geld", "order_zending", "vervoerder", "klant", "wekker"];

interface Props {
  zaakId: string;
  klantId: string | null;
}

export function ActiesMenu({ zaakId, klantId }: Props) {
  const [open, setOpen] = useState(false);
  const [voorstel, setVoorstel] = useState<{
    soort: ActieSoort;
    actie: WerkplekActie;
  } | null>(null);
  const [uitkomst, setUitkomst] = useState<WerkplekActie | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const omhulsel = useRef<HTMLDivElement | null>(null);
  // De knop in state en niet in een ref: het zwevende menu heeft het
  // element nodig om zich te positioneren, en een ref geeft geen
  // hertekening als hij gevuld wordt.
  const [knop, setKnop] = useState<HTMLButtonElement | null>(null);
  const client = useQueryClient();

  const soorten = useQuery({
    queryKey: ["werkplek", "actiesoorten"],
    queryFn: werkplekApi.actiesoorten,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!open) return;
    function buiten(gebeurtenis: MouseEvent) {
      const doel = gebeurtenis.target as Node;
      // Het menu hangt sinds sessie 42 in een portal aan `document.body`
      // en zit dus niet meer ín het omhulsel. Een klik erin mag hem niet
      // sluiten, dus die wordt hier apart herkend.
      if (omhulsel.current?.contains(doel)) return;
      if ((doel as HTMLElement)?.closest?.(".wp-actiemenu-zwevend")) return;
      setOpen(false);
    }
    function ontsnap(gebeurtenis: KeyboardEvent) {
      if (gebeurtenis.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", ontsnap);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", ontsnap);
    };
  }, [open]);

  const stelVoor = useMutation({
    mutationFn: (soort: ActieSoort) =>
      werkplekApi.stelActieVoor({
        soort: soort.soort,
        zaak_id: zaakId,
        klant_id: klantId,
      }),
  });

  const bevestig = useMutation({
    mutationFn: (actieId: string) => werkplekApi.bevestigActie(actieId),
    onSuccess: (actie) => {
      setUitkomst(actie);
      setVoorstel(null);
      void client.invalidateQueries({ queryKey: ["werkplek", "zaak", zaakId] });
    },
    onError: () =>
      setFout("De actie is niet uitgevoerd. Probeer het opnieuw."),
  });

  // Sessie 47 (4a): "Geen wekker. Zet er een" in De zaak opent dit menu.
  useEffect(() => {
    function opOpen(e: Event) {
      const doel = (e as CustomEvent<{ zaakId?: string }>).detail;
      if (doel?.zaakId === zaakId) setOpen(true);
    }
    window.addEventListener("wp-acties-open", opOpen);
    return () => window.removeEventListener("wp-acties-open", opOpen);
  }, [zaakId]);

  // Sessie 47 (2c): afronden zonder antwoord kan ook zonder tegel.
  const afronden = useAfronden(zaakId);
  const [afrondOpen, setAfrondOpen] = useState(false);

  const gegroepeerd = GROEPVOLGORDE.map((groep) => ({
    groep,
    rijen: (soorten.data ?? []).filter((rij) => rij.groep === groep),
  })).filter((blok) => blok.rijen.length > 0);

  return (
    <span className="wp-actiemenu" ref={omhulsel}>
      <button
        type="button"
        ref={setKnop}
        className="wp-knop"
        data-acties
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((aan) => !aan)}
      >
        <IcoonBeheer maat={13} />
        Acties
        <IcoonPijlOnder maat={12} />
      </button>

      <Zwevendmenu anker={knop} open={open} sluit={() => setOpen(false)}>
        <>
          {soorten.isLoading ? (
            <div className="wp-kop">Bezig met laden…</div>
          ) : gegroepeerd.length === 0 ? (
            <div className="wp-kop">Geen acties beschikbaar</div>
          ) : (
            gegroepeerd.map(({ groep, rijen }) => (
              <div key={groep}>
                <div className="wp-kop">{GROEPKOPPEN[groep] ?? groep}</div>
                {rijen.map((rij) => {
                  const uit = rij.stand === "nog_niet_aangesloten";
                  return (
                    <button
                      key={rij.soort}
                      type="button"
                      role="menuitem"
                      data-actie={rij.soort}
                      disabled={uit}
                      title={uit ? (rij.reden_uit ?? undefined) : undefined}
                      onClick={() => {
                        setFout(null);
                        setUitkomst(null);
                        stelVoor.mutate(rij, {
                          onSuccess: (actie) => {
                            setVoorstel({ soort: rij, actie });
                            setOpen(false);
                          },
                          onError: () =>
                            setFout(
                              "Het voorstel kon niet worden klaargezet.",
                            ),
                        });
                      }}
                    >
                      {rij.label}
                      {uit ? (
                        <small data-reden-uit={rij.soort}>
                          {rij.reden_uit ?? "Nog niet aangesloten"}
                        </small>
                      ) : rij.stand === "droog" ? (
                        <small>draait droog</small>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
          <div>
            <div className="wp-kop">Zaak</div>
            <button
              type="button"
              role="menuitem"
              data-actie="afronden_zonder_antwoord"
              onClick={() => {
                setFout(null);
                setUitkomst(null);
                setVoorstel(null);
                setAfrondOpen(true);
                setOpen(false);
              }}
            >
              Afronden zonder antwoord
              <small>geen bericht aan de klant, reden op de tijdlijn</small>
            </button>
          </div>
        </>
      </Zwevendmenu>

      {afrondOpen ? (
        <AfrondKeuze
          staat={afronden}
          plek="onder"
          onKlaar={() => setAfrondOpen(false)}
        />
      ) : null}
      {afronden.uitkomst ? (
        <div className="wp-afrond-zwevend">
          <AfrondBalk staat={afronden} />
        </div>
      ) : null}

      {voorstel ? (
        <div className="wp-blok" data-bevestigstap style={{ marginTop: 8 }}>
          <h4>Bevestigen</h4>
          <div style={{ padding: "10px 14px", fontSize: 13 }}>
            <p style={{ margin: "0 0 8px" }}>
              {voorstel.actie.bevestigtekst ??
                `${voorstel.soort.label} uitvoeren voor deze zaak?`}
            </p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button
                type="button"
                className="wp-knop wp-knop-primair"
                data-bevestig
                disabled={bevestig.isPending}
                onClick={() => bevestig.mutate(voorstel.actie.id)}
              >
                {bevestig.isPending ? "Bezig…" : "Ja, doe het"}
              </button>
              <button
                type="button"
                className="wp-knop"
                onClick={() => {
                  void werkplekApi.annuleerActie(voorstel.actie.id);
                  setVoorstel(null);
                }}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {uitkomst ? (
        <div className="wp-blok" data-actie-uitkomst style={{ marginTop: 8 }}>
          <div style={{ padding: "10px 14px", fontSize: 13 }}>
            {uitkomst.droog
              ? "Klaargezet, maar nog niet doorgevoerd: deze actie draait droog tot JP de vlag openzet."
              : uitkomst.foutmelding
                ? `Mislukt: ${uitkomst.foutmelding}`
                : "Uitgevoerd. De regel staat op de tijdlijn."}
          </div>
        </div>
      ) : null}

      {fout ? (
        <div
          className="wp-blok"
          role="alert"
          style={{ marginTop: 8, padding: "10px 14px", fontSize: 13 }}
        >
          {fout}
        </div>
      ) : null}
    </span>
  );
}
